import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Remove,
  Casino,
  Check,
  Close,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { createWinningPattern, updateWinningPattern } from '../../store/slices/winningPatternsSlice';
import { WinningPatternData, WinningPatternForm } from '../../types';
import { toast } from 'react-toastify';

const schema = yup.object().shape({
  name: yup.string().required('Pattern name is required'),
  description: yup.string().required('Description is required'),
  patternType: yup.string().oneOf(['builtin', 'custom']).required('Pattern type is required'),
  builtinType: yup.string().when('patternType', {
    is: 'builtin',
    then: () => yup.string().oneOf(['standard', 'fourCorners', 'fullHouse', 'diamond', 'xPattern']).required('Built-in type is required'),
    otherwise: () => yup.string().optional(),
  }),
  customPattern: yup.array().when('patternType', {
    is: 'custom',
    then: () => yup.array().of(yup.array().of(yup.number())).length(5).required('Custom pattern is required'),
    otherwise: () => yup.array().optional(),
  }),
  visualPattern: yup.string().optional(),
});

type FormData = WinningPatternForm;

interface WinningPatternFormModalProps {
  pattern?: WinningPatternData | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const WinningPatternFormModal: React.FC<WinningPatternFormModalProps> = ({
  pattern,
  open,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);

  const isEditing = !!pattern;

  const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<FormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: pattern?.name || '',
      description: pattern?.description || '',
      patternType: pattern?.patternType || 'custom',
      builtinType: pattern?.builtinType || 'standard',
      customPattern: pattern?.customPattern || [
        [0, 0, 0, 0, 0],
        [0, 0, 0, 0, 0],
        [0, 0, 1, 0, 0],
        [0, 0, 0, 0, 0],
        [0, 0, 0, 0, 0],
      ],
      visualPattern: pattern?.visualPattern || '',
    },
  });

  const watchedPatternType = watch('patternType');
  const watchedCustomPattern = watch('customPattern');
  const watchedBuiltinType = watch('builtinType');

  useEffect(() => {
    if (open) {
      reset({
        name: pattern?.name || '',
        description: pattern?.description || '',
        patternType: pattern?.patternType || 'custom',
        builtinType: pattern?.builtinType || 'standard',
        customPattern: pattern?.customPattern || [
          [0, 0, 0, 0, 0],
          [0, 0, 0, 0, 0],
          [0, 0, 1, 0, 0],
          [0, 0, 0, 0, 0],
          [0, 0, 0, 0, 0],
        ],
        visualPattern: pattern?.visualPattern || '',
      });
    }
  }, [open, pattern, reset]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      if (isEditing && pattern) {
        const result = await dispatch(updateWinningPattern({ patternId: pattern.id!, patternData: data }) as any);
        if (result.payload) {
          toast.success('Winning pattern updated successfully');
          onSuccess();
          onClose();
        }
      } else {
        const result = await dispatch(createWinningPattern(data) as any);
        if (result.payload) {
          toast.success('Winning pattern created successfully');
          onSuccess();
          onClose();
        }
      }
    } catch (error) {
      toast.error(isEditing ? 'Failed to update winning pattern' : 'Failed to create winning pattern');
    } finally {
      setLoading(false);
    }
  };

  const handleCellToggle = (rowIndex: number, colIndex: number) => {
    if (watchedPatternType !== 'custom') return;
    
    const currentPattern = watchedCustomPattern || [
      [0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0],
      [0, 0, 1, 0, 0],
      [0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0],
    ];
    
    const newPattern = currentPattern.map((row, rIndex) =>
      rIndex === rowIndex
        ? row.map((cell, cIndex) => cIndex === colIndex ? (cell === 1 ? 0 : 1) : cell)
        : row
    );
    
    // Update the form value
    setValue('customPattern', newPattern);
  };

  const renderCustomPatternGrid = () => {
    if (watchedPatternType !== 'custom') return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Custom Pattern (5x5 Grid)
        </Typography>
        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
          Click squares to toggle between required (filled) and optional (empty)
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: { xs: 0.5, sm: 1 }, 
          mt: 2,
          overflowX: 'auto',
          padding: { xs: 1, sm: 0 }
        }}>
          {watchedCustomPattern?.map((row, rowIndex) => (
            <Box key={rowIndex} sx={{ 
              display: 'flex', 
              gap: { xs: 0.5, sm: 1 },
              justifyContent: 'center'
            }}>
              {row.map((cell, colIndex) => (
                <Box
                  key={colIndex}
                  sx={{
                    width: { xs: 32, sm: 40 },
                    height: { xs: 32, sm: 40 },
                    backgroundColor: cell === 1 ? 'primary.main' : 'grey.200',
                    border: '2px solid',
                    borderColor: cell === 1 ? 'primary.dark' : 'grey.400',
                    borderRadius: 1,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      backgroundColor: cell === 1 ? 'primary.dark' : 'grey.300',
                    },
                  }}
                  onClick={() => handleCellToggle(rowIndex, colIndex)}
                >
                  {cell === 1 && <Check sx={{ color: 'white', fontSize: 20 }} />}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const getBuiltinTypeDescription = (type: string) => {
    switch (type) {
      case 'standard': return 'Standard bingo - any row, column, or diagonal';
      case 'fourCorners': return 'Four corner squares must be marked';
      case 'fullHouse': return 'All squares on the card must be marked';
      case 'diamond': return 'Diamond pattern in the center';
      case 'xPattern': return 'X pattern across the card';
      default: return '';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
      sx={{
        '& .MuiDialog-paper': {
          margin: { xs: 1, sm: 2 },
          maxHeight: { xs: 'calc(100dvh - 16px)', sm: '90vh' },
          width: { xs: 'calc(100% - 16px)', sm: 'auto' },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
     >
      <DialogTitle sx={{ 
        padding: { xs: 2, sm: 3 },
        paddingBottom: { xs: 1, sm: 2 }
      }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          {isEditing ? 'Edit Winning Pattern' : 'Create Winning Pattern'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0, 
        padding: { xs: 2, sm: 3 },
        '&.MuiDialogContent-root': {
          paddingTop: { xs: 1, sm: 2 },
        }
      }}>
        <Box sx={{ mt: { xs: 0, sm: 2 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 2, sm: 3 }, 
              flexWrap: 'wrap',
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 250 } }}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Pattern Name"
                      placeholder="Custom Cross Pattern"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 250 } }}>
                <Controller
                  name="patternType"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.patternType}>
                      <InputLabel>Pattern Type</InputLabel>
                      <Select {...field} label="Pattern Type">
                        <MenuItem value="builtin">Built-in Pattern</MenuItem>
                        <MenuItem value="custom">Custom Pattern</MenuItem>
                      </Select>
                      {errors.patternType && (
                        <Typography variant="caption" color="error">
                          {errors.patternType.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Box>
            </Box>
            <Box>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Description"
                    placeholder="Describe the winning pattern..."
                    multiline
                    rows={3}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                  />
                )}
              />
            </Box>
            {watchedPatternType === 'builtin' && (
              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 2, sm: 3 }, 
                flexWrap: 'wrap',
                flexDirection: { xs: 'column', sm: 'row' }
              }}>
                <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 250 } }}>
                  <Controller
                    name="builtinType"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.builtinType}>
                        <InputLabel>Built-in Type</InputLabel>
                        <Select {...field} label="Built-in Type">
                          <MenuItem value="standard">Standard</MenuItem>
                          <MenuItem value="fourCorners">Four Corners</MenuItem>
                          <MenuItem value="fullHouse">Full House</MenuItem>
                          <MenuItem value="diamond">Diamond</MenuItem>
                          <MenuItem value="xPattern">X Pattern</MenuItem>
                        </Select>
                        {errors.builtinType && (
                          <Typography variant="caption" color="error">
                            {errors.builtinType.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                  {watchedBuiltinType && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      {getBuiltinTypeDescription(watchedBuiltinType)}
                    </Alert>
                  )}
                </Box>
              </Box>
            )}
            <Box>
              <Controller
                name="visualPattern"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Visual Pattern Description"
                    placeholder="Cross pattern with center row and center column"
                    error={!!errors.visualPattern}
                    helperText={errors.visualPattern?.message || 'Optional description for UI display'}
                  />
                )}
              />
            </Box>
            {renderCustomPatternGrid()}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        flexDirection: { xs: 'column', sm: 'row' }, 
        gap: { xs: 1, sm: 0 },
        padding: { xs: 2, sm: 1 },
        '& > *': {
          width: { xs: '100%', sm: 'auto' },
        }
      }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          variant="outlined"
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          {loading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Pattern' : 'Create Pattern')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WinningPatternFormModal; 